/**
 * Texto y stickers del Timeline — capa nativa, editable y persistente.
 *
 * Todo sale del modelo canónico (`utils/overlayModel.js`): el preview usa las
 * MISMAS fórmulas relativas que el rasterizador del render, así que lo que se
 * ve acá es lo que sale en el MP4.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Type, Sticker as StickerIcon, Trash2, Copy, Eye, EyeOff,
  ArrowUp, ArrowDown, Loader2, X, Plus, BookmarkPlus, GripVertical, Star,
} from 'lucide-react';
import { toast } from 'sonner';
// Envío UI-3 · pestañas del panel de texto y "Aplicar estilo de marca" (layout nuevo)
import { LAYOUT_V2 } from '../../layout/layoutV2';
import { brandTextPatch } from '../../layout/brandStyle';
import { useBrandStore } from '../../store/brandStore';
import { useWorkflowStore } from '../../store/workflowStore';
import useTimelineStore, { usePxPerSec } from '../../store/timelineStore';
import {
  FONT_LIBRARY, TEXT_PRESETS, textPreviewStyles, stickerPreviewStyles,
} from '../../utils/overlayModel';
import { galleryService } from '../../services/galleryService';
import { stickerService } from '../../services/stickerService';
import { PAGE, categoriesOf, filterStickers } from './stickerLibraryModel';

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// ────────────────────────────────────────────────────────────────
// Elemento sobre el preview: mover, escalar y rotar directamente
// ────────────────────────────────────────────────────────────────
export const ElementInPreview = ({ element, isSelected, onSelect, onChange,
                                   canvasWidth = 640, canvasHeight = 360,
                                   layerZ = 10 }) => {
  const rootRef = useRef(null);
  const [drag, setDrag] = useState(null);

  const startDrag = (e, mode) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect();
    const container = rootRef.current?.parentElement;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    setDrag({
      mode,
      containerW: rect.width,
      containerH: rect.height,
      containerLeft: rect.left,
      containerTop: rect.top,
      startX: e.clientX,
      startY: e.clientY,
      start: { ...element.transform, font_size_pct: element.style?.font_size_pct },
    });
  };

  useEffect(() => {
    if (!drag) return undefined;
    const onMove = (e) => {
      const dx = (e.clientX - drag.startX) / drag.containerW;
      const dy = (e.clientY - drag.startY) / drag.containerH;
      if (drag.mode === 'move') {
        onChange({
          transform: {
            x_pct: clamp(drag.start.x_pct + dx, 0, 1),
            y_pct: clamp(drag.start.y_pct + dy, 0, 1),
          },
        });
      } else if (drag.mode === 'scale') {
        const factor = Math.hypot(dx, dy) * (dx + dy > 0 ? 1 : -1);
        if (element.type === 'text') {
          onChange({
            style: {
              font_size_pct: clamp((drag.start.font_size_pct || 0.075) + factor * 0.35,
                0.01, 0.5),
            },
          });
        } else {
          onChange({ transform: { scale: clamp(drag.start.scale + factor * 1.2, 0.02, 3) } });
        }
      } else if (drag.mode === 'rotate') {
        const cx = drag.containerLeft + drag.start.x_pct * drag.containerW;
        const cy = drag.containerTop + drag.start.y_pct * drag.containerH;
        const angle = (Math.atan2(e.clientY - cy, e.clientX - cx) * 180) / Math.PI + 90;
        onChange({ transform: { rotation: Math.round(angle) } });
      }
    };
    const onUp = () => setDrag(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, onChange, element.type]);

  if (element.type === 'sticker') {
    return (
      <div
        ref={rootRef}
        className={`tl-element tl-element-sticker ${isSelected ? 'is-selected' : ''}`}
        style={{ ...stickerPreviewStyles(element), zIndex: layerZ }}
        onMouseDown={(e) => (isSelected ? startDrag(e, 'move') : (e.stopPropagation(), onSelect()))}
        data-testid={`tl-element-${element.element_id}`}
      >
        <img src={element.source_url} alt={element.label || 'sticker'} draggable={false} />
        {isSelected && <ElementHandles onStart={startDrag} />}
      </div>
    );
  }

  const styles = textPreviewStyles(element, canvasHeight, canvasWidth);
  return (
    <div
      ref={rootRef}
      className={`tl-element tl-element-text ${isSelected ? 'is-selected' : ''}`}
      style={{ ...styles.wrapper, zIndex: layerZ }}
      onMouseDown={(e) => (isSelected ? startDrag(e, 'move') : (e.stopPropagation(), onSelect()))}
      data-testid={`tl-element-${element.element_id}`}
    >
      <span style={styles.text} data-testid={`tl-element-text-${element.element_id}`}>
        {element.style?.content || ' '}
      </span>
      {isSelected && <ElementHandles onStart={startDrag} />}
    </div>
  );
};

const ElementHandles = ({ onStart }) => (
  <>
    <div className="tl-element-handle rotate" onMouseDown={(e) => onStart(e, 'rotate')} title="Rotar" />
    <div className="tl-element-handle se" onMouseDown={(e) => onStart(e, 'scale')} title="Escalar" />
  </>
);

// ────────────────────────────────────────────────────────────────
// Pista de elementos en el Timeline (inicio y duración editables)
// ────────────────────────────────────────────────────────────────
const ElementClip = ({ element, isSelected, onSelect }) => {
  const pxPerSec = usePxPerSec();
  const updateElement = useTimelineStore((s) => s.updateElement);
  // E6 · Timeline en vivo: pulso ~1,5 s cuando LMX acaba de tocar este elemento.
  const flashing = useTimelineStore((s) => s.agentFlash.includes(element.element_id));
  const [dragging, setDragging] = useState(false);

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const startX = e.clientX;
    const startTime = element.start;
    let moved = false;
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      if (!moved && Math.abs(dx) > 2) { moved = true; setDragging(true); }
      if (moved) updateElement(element.element_id, { start: Math.max(0, startTime + dx / pxPerSec) });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setDragging(false);
      if (!moved) onSelect();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startDur = element.duration;
    const onMove = (ev) => {
      const next = Math.max(0.1, startDur + (ev.clientX - startX) / pxPerSec);
      updateElement(element.element_id, { duration: next });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const Icon = element.type === 'sticker' ? StickerIcon : Type;
  return (
    <div
      className={`tl-clip tl-clip-${element.type} ${isSelected ? 'selected' : ''} ${dragging ? 'dragging' : ''} ${element.visible ? '' : 'is-hidden'} ${flashing ? 'lmx-flash' : ''}`}
      style={{ width: Math.max(element.duration * pxPerSec, 36), left: element.start * pxPerSec }}
      onMouseDown={onMouseDown}
      data-testid={`tl-element-clip-${element.element_id}`}
      title={`${element.label || element.type} · ${element.start.toFixed(1)}s → ${(element.start + element.duration).toFixed(1)}s`}
    >
      <div className="tl-clip-label">
        <Icon size={12} />
        <span>
          {element.type === 'text'
            ? (element.style?.content || 'Texto').slice(0, 24)
            : (element.label || 'Sticker')}
        </span>
      </div>
      <div className="tl-clip-duration">{element.duration.toFixed(1)}s</div>
      <div className="tl-clip-resize" onMouseDown={onResize} data-testid={`tl-element-resize-${element.element_id}`} />
    </div>
  );
};

export const ElementTrack = ({ type, totalWidth, onAdd, onReorder }) => {
  const elements = useTimelineStore((s) => s.elements);
  const selectedClip = useTimelineStore((s) => s.selectedClip);
  const selectClip = useTimelineStore((s) => s.selectClip);
  const list = elements.filter((el) => el.type === type);
  const label = type === 'text' ? 'Texto' : 'Stickers';
  const Icon = type === 'text' ? Type : StickerIcon;

  return (
    <div
      className={`tl-track tl-track-${type}`}
      data-testid={`tl-track-${type}`}
      data-track={type}
    >
      <div
        className="tl-track-label"
        onMouseDown={(e) => onReorder?.(e, type)}
        title="Arrastrá para subir o bajar la capa (arriba = encima)"
      >
        <GripVertical size={11} className="tl-track-grip" />
        <Icon size={13} />
        <span>{label}</span>
        <button
          type="button"
          className="tl-track-add-btn"
          onClick={(e) => { e.stopPropagation(); onAdd?.(type); }}
          aria-label={`Agregar ${label}`}
          data-testid={`tl-track-add-${type}`}
          data-tooltip={type === 'text' ? 'Agregar texto' : 'Agregar sticker'}
        >
          <Plus size={10} strokeWidth={2.5} />
        </button>
      </div>
      <div className="tl-track-lane" style={{ width: totalWidth }}>
        {list.map((el) => (
          <ElementClip
            key={el.element_id}
            element={el}
            isSelected={selectedClip?.id === el.element_id}
            onSelect={() => selectClip({ trackKind: el.type, id: el.element_id })}
          />
        ))}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// Inspector del elemento
// ────────────────────────────────────────────────────────────────
const NumField = ({ label, value, onChange, step = 1, min, max, testId }) => (
  <label className="tl-inspector-field">
    <span>{label}</span>
    <input
      type="number"
      value={Math.round((value ?? 0) * 100) / 100}
      step={step}
      min={min}
      max={max}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      data-testid={testId}
    />
  </label>
);

const ColorField = ({ label, value, onChange, testId }) => (
  <label className="tl-inspector-field tl-inspector-color">
    <span>{label}</span>
    <input type="color" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
  </label>
);

const ToggleField = ({ label, checked, onChange, testId }) => (
  <label className="tl-inspector-field tl-inspector-toggle">
    <span>{label}</span>
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
  </label>
);

const TextTemplates = ({ elementId }) => {
  const templates = useTimelineStore((s) => s.textTemplates);
  const loadTextTemplates = useTimelineStore((s) => s.loadTextTemplates);
  const saveTextTemplate = useTimelineStore((s) => s.saveTextTemplate);
  const applyTextTemplate = useTimelineStore((s) => s.applyTextTemplate);
  const removeTextTemplate = useTimelineStore((s) => s.removeTextTemplate);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');

  useEffect(() => { loadTextTemplates(); }, [loadTextTemplates]);

  const confirm = () => {
    if (!name.trim()) { setNaming(false); return; }
    saveTextTemplate(name.trim(), elementId);
    setName('');
    setNaming(false);
  };

  return (
    <div className="tl-element-templates" data-testid="tl-text-templates">
      <div className="tl-inspector-subhead">
        Mis plantillas de estilo
        <button
          type="button"
          className="tl-template-save"
          onClick={() => setNaming((v) => !v)}
          data-testid="tl-template-save-toggle"
          title="Guardar este estilo como plantilla reutilizable en cualquier proyecto"
        >
          <BookmarkPlus size={12} /> Guardar estilo
        </button>
      </div>

      {naming && (
        <div className="tl-template-namer">
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') confirm(); }}
            placeholder="Nombre de la plantilla"
            maxLength={40}
            data-testid="tl-template-name"
          />
          <button type="button" onClick={confirm} data-testid="tl-template-confirm">
            Guardar
          </button>
        </div>
      )}

      <div className="tl-template-chips">
        {templates.length === 0 && (
          <span className="tl-template-empty">
            Todavía no guardaste estilos. Armá un texto como te gusta y guardalo.
          </span>
        )}
        {templates.map((template) => (
          <span key={template.id} className="tl-template-chip">
            <button
              type="button"
              onClick={() => applyTextTemplate(template.id, elementId)}
              data-testid={`tl-template-apply-${template.id}`}
              title="Aplicar esta plantilla al texto seleccionado"
            >
              {template.name}
            </button>
            <button
              type="button"
              className="tl-template-chip-remove"
              onClick={() => removeTextTemplate(template.id)}
              data-testid={`tl-template-remove-${template.id}`}
              title="Borrar plantilla"
            >
              <X size={10} />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};

export const ElementInspector = ({ element }) => {
  const updateElement = useTimelineStore((s) => s.updateElement);
  const updateElementStyle = useTimelineStore((s) => s.updateElementStyle);
  const updateElementTransform = useTimelineStore((s) => s.updateElementTransform);
  const applyElementPreset = useTimelineStore((s) => s.applyElementPreset);
  const removeElement = useTimelineStore((s) => s.removeElement);
  const duplicateElement = useTimelineStore((s) => s.duplicateElement);
  const moveElementLayer = useTimelineStore((s) => s.moveElementLayer);
  const toggleElementVisible = useTimelineStore((s) => s.toggleElementVisible);
  // UI-3 · pestañas (sólo layout nuevo) y marca del proyecto
  const [tab, setTab] = useState('texto');
  const brand = useBrandStore((s) => s.brand);
  const loadBrand = useBrandStore((s) => s.load);
  const projectId = useWorkflowStore((s) => s.currentProjectId);
  useEffect(() => { if (LAYOUT_V2 && projectId) loadBrand(projectId); }, [projectId, loadBrand]);
  if (!element) return null;

  const id = element.element_id;
  const isText = element.type === 'text';
  const style = element.style || {};
  const transform = element.transform || {};

  return (
    <div
      className="tl-inspector tl-inspector-element"
      data-testid="tl-element-inspector"
      data-tab={LAYOUT_V2 && element.type === 'text' ? tab : undefined}
    >
      <div className="tl-inspector-header">
        <span className="tl-inspector-title">
          {isText ? <Type size={14} /> : <StickerIcon size={14} />}
          {isText ? 'Texto' : 'Sticker'} · capa {element.z_order}
        </span>
        <div className="tl-inspector-actions">
          <button className="tl-icon-btn" onClick={() => moveElementLayer(id, 1)} title="Subir capa" data-testid="tl-element-layer-up"><ArrowUp size={14} /></button>
          <button className="tl-icon-btn" onClick={() => moveElementLayer(id, -1)} title="Bajar capa" data-testid="tl-element-layer-down"><ArrowDown size={14} /></button>
          <button className="tl-icon-btn" onClick={() => toggleElementVisible(id)} title={element.visible ? 'Ocultar' : 'Mostrar'} data-testid="tl-element-visible">
            {element.visible ? <Eye size={14} /> : <EyeOff size={14} />}
          </button>
          <button className="tl-icon-btn" onClick={() => duplicateElement(id)} title="Duplicar" data-testid="tl-element-duplicate"><Copy size={14} /></button>
          <button className="tl-icon-btn danger" onClick={() => removeElement(id)} title="Eliminar" data-testid="tl-element-remove"><Trash2 size={14} /></button>
        </div>
      </div>

      {LAYOUT_V2 && isText && (
        <div className="tl-insp-tabs" role="tablist" aria-label="Secciones del texto">
          {[['texto', 'Texto'], ['pos', 'Posición'], ['fx', 'Efectos'], ['tpl', 'Estilos']].map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k}
              className={tab === k ? 'active' : ''} onClick={() => setTab(k)} data-testid={`tl-insp-tab-${k}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {LAYOUT_V2 && isText && brand && (
        <button
          type="button"
          className="tl-brand-apply"
          onClick={() => {
            const use = (style.font_size_pct || 0) >= 0.06 ? 'titles' : 'body';
            const { patch, missingFont } = brandTextPatch(brand, use);
            updateElementStyle(id, patch);
            if (missingFont) toast.info(`«${missingFont}» no está disponible en el video: se dejó la tipografía actual.`);
            else toast.success(`Estilo de @${brand.handle} aplicado`);
          }}
          data-testid="tl-brand-apply"
        >
          <Star size={13} /> Aplicar estilo de marca
        </button>
      )}

      {isText && (
        <>
          <div className="tl-element-presets" data-testid="tl-element-presets">
            {Object.entries(TEXT_PRESETS).map(([key, preset]) => (
              <button
                key={key}
                type="button"
                className={`tl-element-preset ${style.preset === key ? 'active' : ''}`}
                onClick={() => applyElementPreset(id, key)}
                data-testid={`tl-element-preset-${key}`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <textarea
            className="tl-element-content"
            value={style.content || ''}
            onChange={(e) => updateElementStyle(id, { content: e.target.value, preset: 'custom' })}
            rows={2}
            placeholder="Escribí el texto…"
            data-testid="tl-element-content"
          />
        </>
      )}

      <div className="tl-inspector-grid">
        <NumField label="Inicio (s)" value={element.start} step={0.1} min={0}
          onChange={(v) => updateElement(id, { start: v })} testId="tl-element-start" />
        <NumField label="Duración (s)" value={element.duration} step={0.1} min={0.1}
          onChange={(v) => updateElement(id, { duration: v })} testId="tl-element-duration" />
        <NumField label="Pos X (%)" value={transform.x_pct * 100} step={1} min={0} max={100}
          onChange={(v) => updateElementTransform(id, { x_pct: v / 100 })} testId="tl-element-x" />
        <NumField label="Pos Y (%)" value={transform.y_pct * 100} step={1} min={0} max={100}
          onChange={(v) => updateElementTransform(id, { y_pct: v / 100 })} testId="tl-element-y" />
        <NumField label="Rotación (°)" value={transform.rotation} step={1} min={-360} max={360}
          onChange={(v) => updateElementTransform(id, { rotation: v })} testId="tl-element-rotation" />
        <NumField label="Opacidad (%)" value={transform.opacity * 100} step={1} min={0} max={100}
          onChange={(v) => updateElementTransform(id, { opacity: v / 100 })} testId="tl-element-opacity" />
        {!isText && (
          <NumField label="Escala (%)" value={transform.scale * 100} step={1} min={2} max={300}
            onChange={(v) => updateElementTransform(id, { scale: v / 100 })} testId="tl-element-scale" />
        )}
        {isText && (
          <>
            <label className="tl-inspector-field">
              <span>Tipografía</span>
              <select
                value={style.font_family}
                onChange={(e) => updateElementStyle(id, { font_family: e.target.value, preset: 'custom' })}
                data-testid="tl-element-font"
              >
                {Object.entries(FONT_LIBRARY).map(([key, font]) => (
                  <option key={key} value={key}>{font.label}</option>
                ))}
              </select>
            </label>
            <NumField label="Tamaño (% alto)" value={style.font_size_pct * 100} step={0.5} min={1} max={50}
              onChange={(v) => updateElementStyle(id, { font_size_pct: v / 100, preset: 'custom' })} testId="tl-element-size" />
            <label className="tl-inspector-field">
              <span>Peso</span>
              <select
                value={style.font_weight}
                onChange={(e) => updateElementStyle(id, { font_weight: Number(e.target.value), preset: 'custom' })}
                data-testid="tl-element-weight"
              >
                {[300, 400, 500, 600, 700, 800, 900].map((w) => (
                  <option key={w} value={w}>{w}</option>
                ))}
              </select>
            </label>
            <label className="tl-inspector-field">
              <span>Alineación</span>
              <select
                value={style.align}
                onChange={(e) => updateElementStyle(id, { align: e.target.value, preset: 'custom' })}
                data-testid="tl-element-align"
              >
                <option value="left">Izquierda</option>
                <option value="center">Centro</option>
                <option value="right">Derecha</option>
              </select>
            </label>
            <ColorField label="Color" value={style.color} testId="tl-element-color"
              onChange={(v) => updateElementStyle(id, { color: v, preset: 'custom' })} />
            <NumField label="Ancho caja (%)" value={style.max_width_pct * 100} step={1} min={10} max={100}
              onChange={(v) => updateElementStyle(id, { max_width_pct: v / 100, preset: 'custom' })} testId="tl-element-maxwidth" />
            <ToggleField label="MAYÚSCULAS" checked={style.uppercase} testId="tl-element-uppercase"
              onChange={(v) => updateElementStyle(id, { uppercase: v, preset: 'custom' })} />
          </>
        )}
      </div>

      {isText && (
        <div className="tl-element-effects">
          <div className="tl-inspector-subhead">Sombra · contorno · caja de fondo</div>
          <div className="tl-inspector-grid">
            <ToggleField label="Sombra" checked={style.shadow?.enabled} testId="tl-element-shadow-on"
              onChange={(v) => updateElementStyle(id, { shadow: { enabled: v }, preset: 'custom' })} />
            <ColorField label="Color sombra" value={style.shadow?.color || '#000000'} testId="tl-element-shadow-color"
              onChange={(v) => updateElementStyle(id, { shadow: { color: v } })} />
            <NumField label="Desenfoque (%)" value={(style.shadow?.blur_pct || 0) * 100} step={1} min={0} max={100}
              onChange={(v) => updateElementStyle(id, { shadow: { blur_pct: v / 100 } })} testId="tl-element-shadow-blur" />
            <ToggleField label="Contorno" checked={style.outline?.enabled} testId="tl-element-outline-on"
              onChange={(v) => updateElementStyle(id, { outline: { enabled: v }, preset: 'custom' })} />
            <ColorField label="Color contorno" value={style.outline?.color || '#000000'} testId="tl-element-outline-color"
              onChange={(v) => updateElementStyle(id, { outline: { color: v } })} />
            <NumField label="Grosor (%)" value={(style.outline?.width_pct || 0) * 100} step={1} min={0} max={50}
              onChange={(v) => updateElementStyle(id, { outline: { width_pct: v / 100 } })} testId="tl-element-outline-width" />
            <ToggleField label="Caja de fondo" checked={style.box?.enabled} testId="tl-element-box-on"
              onChange={(v) => updateElementStyle(id, { box: { enabled: v }, preset: 'custom' })} />
            <ColorField label="Color caja" value={style.box?.color || '#000000'} testId="tl-element-box-color"
              onChange={(v) => updateElementStyle(id, { box: { color: v } })} />
            <NumField label="Opacidad caja (%)" value={(style.box?.opacity ?? 0.55) * 100} step={1} min={0} max={100}
              onChange={(v) => updateElementStyle(id, { box: { opacity: v / 100 } })} testId="tl-element-box-opacity" />
          </div>
        </div>
      )}

      {isText && <TextTemplates elementId={id} />}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// Biblioteca de stickers: propia + Fluent Emoji (R2) + assets del usuario
// Envío AP-7 · estilos (3D / Plano / Propios), categorías y buscador.
// ────────────────────────────────────────────────────────────────
const STYLE_TABS = [['3d', '3D'], ['flat', 'Plano'], ['sml', 'Propios']];

export const StickerPicker = ({ onClose, onPick }) => {
  const [tab, setTab] = useState('library');
  const [style, setStyle] = useState('3d');
  const [library, setLibrary] = useState(null);
  const [packs, setPacks] = useState([]);
  const [assets, setAssets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState('all');
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(PAGE);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const load = tab === 'library'
      ? stickerService.listLibrary({ style }).then((data) => {
        if (cancelled) return;
        const hasFluent = (data.packs || []).some((p) => p.id === 'fluent');
        // Sin el pack publicado, la biblioteca son sólo los propios (como antes).
        if (!hasFluent && style !== 'sml') { setStyle('sml'); return; }
        setPacks(data.packs || []);
        setLibrary(data.stickers || []);
      })
      : galleryService.listAssets({ assetType: 'image', limit: 60 })
        .then((data) => { if (!cancelled) setAssets(data.assets || []); });
    load.catch(() => { if (!cancelled) (tab === 'library' ? setLibrary([]) : setAssets([])); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tab, style]);

  useEffect(() => { setLimit(PAGE); }, [style, cat, query, tab]);

  const fluent = packs.find((p) => p.id === 'fluent');
  const cats = tab === 'library' && style !== 'sml' && fluent
    ? categoriesOf(library, fluent.categories) : [];
  const filtered = tab === 'library' ? filterStickers(library, { cat, q: query }) : null;
  const items = tab === 'library'
    ? filtered.slice(0, limit).map((s) => ({ id: s.id, url: s.url, name: s.label }))
    : (assets || []).map((a) => ({ id: a.id, url: a.url, name: a.filename, assetId: a.id }));
  const more = tab === 'library' ? filtered.length - items.length : 0;

  return (
    <div className="tl-gallery-overlay" onClick={onClose} data-testid="tl-sticker-overlay">
      <div className="tl-gallery-modal tl-sticker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tl-gallery-header">
          <span className="tl-gallery-title">Stickers — PNG con transparencia</span>
          <div className="tl-gallery-tabs">
            <button className={`tl-gallery-tab ${tab === 'library' ? 'active' : ''}`} onClick={() => setTab('library')} data-testid="tl-sticker-tab-library">Biblioteca</button>
            <button className={`tl-gallery-tab ${tab === 'assets' ? 'active' : ''}`} onClick={() => setTab('assets')} data-testid="tl-sticker-tab-assets">Mis assets</button>
          </div>
          <button className="tl-icon-btn" onClick={onClose} data-testid="tl-sticker-close"><X size={14} /></button>
        </div>
        {tab === 'library' && fluent && (
          <div className="tl-sticker-tools">
            <div className="tl-sticker-styles" role="group" aria-label="Estilo">
              {STYLE_TABS.map(([k, label]) => (
                <button key={k} type="button" aria-pressed={style === k}
                  className={style === k ? 'active' : ''}
                  onClick={() => { setStyle(k); setCat('all'); }} data-testid={`tl-sticker-style-${k}`}>{label}</button>
              ))}
            </div>
            <input
              type="search"
              className="tl-sticker-search"
              placeholder="Buscar: corazón, fuego, pulgar…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Buscar stickers"
              data-testid="tl-sticker-search"
            />
            {cats.length > 0 && (
              <div className="tl-sticker-cats" role="group" aria-label="Categorías">
                <button type="button" className={cat === 'all' ? 'active' : ''} aria-pressed={cat === 'all'}
                  onClick={() => setCat('all')}>Todas</button>
                {cats.map(([id, label]) => (
                  <button key={id} type="button" className={cat === id ? 'active' : ''} aria-pressed={cat === id}
                    onClick={() => setCat(id)} data-testid={`tl-sticker-cat-${id}`}>{label}</button>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="tl-gallery-grid tl-sticker-grid">
          {loading && <div className="tl-gallery-empty"><Loader2 size={18} className="spin" /></div>}
          {!loading && items.length === 0 && (
            <div className="tl-gallery-empty">
              {tab === 'library'
                ? (query || cat !== 'all' ? 'No hay stickers con esa búsqueda.' : 'La biblioteca de stickers todavía no está publicada.')
                : 'No hay imágenes en tu galería.'}
            </div>
          )}
          {!loading && items.map((item) => (
            <button
              key={item.id}
              className="tl-gallery-card tl-sticker-card"
              onClick={() => onPick(item)}
              title={item.name}
              data-testid={`tl-sticker-card-${item.id}`}
            >
              <img src={item.url} alt={item.name} loading="lazy" />
              <span className="tl-gallery-filename">{item.name}</span>
            </button>
          ))}
          {!loading && more > 0 && (
            <button type="button" className="tl-sticker-more" onClick={() => setLimit((n) => n + PAGE)}
              data-testid="tl-sticker-more">Mostrar más ({more})</button>
          )}
        </div>
        {tab === 'library' && fluent && style !== 'sml' && (
          <div className="tl-sticker-credit">
            Emoji: <a href={fluent.license_url} target="_blank" rel="noreferrer">Microsoft Fluent Emoji</a> · licencia MIT
          </div>
        )}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// Menú para insertar texto con preset
// ────────────────────────────────────────────────────────────────
export const TextPresetMenu = ({ onClose, onPick }) => (
  <div className="tl-gallery-overlay" onClick={onClose} data-testid="tl-text-preset-overlay">
    <div className="tl-gallery-modal tl-text-preset-modal" onClick={(e) => e.stopPropagation()}>
      <div className="tl-gallery-header">
        <span className="tl-gallery-title">Agregar texto — elegí un estilo</span>
        <button className="tl-icon-btn" onClick={onClose} data-testid="tl-text-preset-close"><X size={14} /></button>
      </div>
      <div className="tl-text-preset-list">
        {Object.entries(TEXT_PRESETS).map(([key, preset]) => (
          <button
            key={key}
            type="button"
            className="tl-text-preset-item"
            onClick={() => onPick(key)}
            data-testid={`tl-text-preset-${key}`}
          >
            <span className={`tl-text-preset-sample sample-${key}`}>{preset.label}</span>
            <span className="tl-text-preset-hint">
              {key === 'subtitle'
                ? 'Estilo visual tipo subtítulo (no es transcripción automática)'
                : 'Estilo listo para usar'}
            </span>
          </button>
        ))}
        <button
          type="button"
          className="tl-text-preset-item"
          onClick={() => onPick('custom')}
          data-testid="tl-text-preset-custom"
        >
          <span className="tl-text-preset-sample">Texto simple</span>
          <span className="tl-text-preset-hint">Sin estilo previo</span>
        </button>
      </div>
    </div>
  </div>
);

export const useElementSelection = () => {
  const selectedClip = useTimelineStore((s) => s.selectedClip);
  const elements = useTimelineStore((s) => s.elements);
  return useCallback(() => {
    if (!selectedClip || !['text', 'sticker'].includes(selectedClip.trackKind)) return null;
    return elements.find((el) => el.element_id === selectedClip.id) || null;
  }, [selectedClip, elements])();
};
